import { Link } from "@tanstack/react-router";
import { ArrowUpRight, Eye, Timer } from "lucide-react";
import { AuthorVerificationBadge, LegalReviewBadge } from "./AgoraBadges";
import type { AgoraArticleCard as Article } from "@/lib/agora/types";

export default function ArticleCard({ article }: { article: Article }) {
  const published = article.published_at
    ? new Date(article.published_at).toLocaleDateString("fr-FR", {
        timeZone: "Europe/Paris",
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : null;

  return (
    <article className="group relative flex h-full flex-col rounded-xl border border-border bg-card p-5 transition-colors hover:border-primary/40">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {article.category && (
          <span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium text-secondary-foreground">
            {article.category.name}
          </span>
        )}
        {article.is_demo && (
          <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-300">
            Exemple
          </span>
        )}
        <LegalReviewBadge
          status={article.legal_review_status}
          lastReviewedAt={article.last_reviewed_at}
        />
      </div>

      <h3 className="text-base font-semibold leading-snug text-foreground">
        <Link
          to="/agora/$slug"
          params={{ slug: article.slug }}
          className="after:absolute after:inset-0 hover:text-primary"
        >
          {article.title}
        </Link>
      </h3>

      {article.primary_question && (
        <p className="mt-2 text-sm italic text-muted-foreground">{article.primary_question}</p>
      )}

      {article.short_answer && (
        <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{article.short_answer}</p>
      )}

      <div className="mt-auto pt-4">
        {article.author && (
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">{article.author.display_name}</span>
            {article.author.profession && <span>· {article.author.profession}</span>}
            <AuthorVerificationBadge
              status={article.author.verification_status}
              trustLevel={article.author.trust_level}
            />
          </div>
        )}
        <div className="mt-2 flex items-center gap-3 text-[11px] text-muted-foreground">
          {published && <span>{published}</span>}
          {article.reading_minutes ? (
            <span className="inline-flex items-center gap-1">
              <Timer className="h-3 w-3" aria-hidden /> {article.reading_minutes} min
            </span>
          ) : null}
          <span className="inline-flex items-center gap-1">
            <Eye className="h-3 w-3" aria-hidden /> {article.views_count}
          </span>
          <ArrowUpRight className="ml-auto h-4 w-4 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden />
        </div>
      </div>
    </article>
  );
}
